import { useLayoutEffect, useRef } from "react";
import { SiteContentContext, assetUrl, siteTheme } from "@/lib/siteContent";
import { sectionContent } from "@/lib/visualEditor";
import Hero from "./Hero";
import Reviews from "./Reviews";
import EditorialMarquee from "./EditorialMarquee";
import Services from "./Services";
import TrustBadges from "./TrustBadges";
import About from "./About";
import Gallery from "./Gallery";
import ServiceArea from "./ServiceArea";
import Owners from "./Owners";
import RequestForm from "./RequestForm";

const components = { hero: Hero, reviews: Reviews, marquee: EditorialMarquee, services: Services, trust: TrustBadges, about: About, gallery: Gallery, areas: ServiceArea, owners: Owners, request: RequestForm };

// Legacy sections contain their own input IDs. Namespace them at the section
// boundary so repeated layouts retain working labels and unique test selectors.
const useSectionNamespace = (ref, id, kind) => useLayoutEffect(() => {
  const scope = ref.current;
  if (!scope) return;
  const prefix = `${id}--`;
  const qualify = (value) => value.startsWith(prefix) ? value : prefix + value;
  const apply = () => {
    scope.querySelectorAll("[id]").forEach((node) => { if (!node.id.startsWith(prefix)) node.id = qualify(node.id); });
    scope.querySelectorAll("[for],[aria-labelledby],[aria-describedby]").forEach((node) => {
      ["for", "aria-labelledby", "aria-describedby"].forEach((key) => {
        const value = node.getAttribute(key);
        if (value) node.setAttribute(key, value.split(" ").map(qualify).join(" "));
      });
    });
    if (id !== kind) scope.querySelectorAll("[data-testid]").forEach((node) => { node.dataset.testid = qualify(node.dataset.testid); });
  };
  apply();
  const observer = new MutationObserver(apply); observer.observe(scope, { childList: true, subtree: true });
  return () => observer.disconnect();
}, [ref, id, kind]);

const CustomSection = ({ section, link }) => {
  const data = section.custom;
  return <section className="py-24 md:py-32" data-testid={`custom-${section.id}`}>
    <div className={`max-w-7xl mx-auto px-6 grid items-center gap-10 ${section.kind === "content" && data.image ? "md:grid-cols-2" : ""}`}>
      <div className={data.image_position === "left" && data.image ? "md:order-2" : ""}>
        {data.eyebrow && <p className="text-electric text-sm mb-4" data-testid={`custom-eyebrow-${section.id}`}>{data.eyebrow}</p>}
        <h2 className="font-bebas text-4xl sm:text-5xl chrome-text" data-testid={`custom-title-${section.id}`}>{data.title}</h2>
        <p className="text-slate-300 mt-5 whitespace-pre-wrap" data-testid={`custom-text-${section.id}`}>{data.text}</p>
        {data.button_label && link(data.button_href) && <a data-testid={`custom-button-${section.id}`} href={link(data.button_href)} className="mt-7 inline-flex bg-electric text-navy-deep font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity">{data.button_label}</a>}
      </div>
      {section.kind === "content" && data.image && <img src={assetUrl(data.image)} alt={data.image_alt} data-testid={`custom-image-${section.id}`} className="w-full aspect-[4/3] object-contain rounded-lg" />}
    </div>
  </section>;
};

export const SectionRenderer = ({ section, site, context, onSelect }) => {
  const ref = useRef(null);
  useSectionNamespace(ref, section.id, section.kind);
  const content = sectionContent(site, section);
  const Component = components[section.kind];
  const style = section.style;
  const sectionTheme = style.background ? siteTheme({ ...site.colors, background: style.background, surface: style.background }) : {};
  if (style.text_color) Object.assign(sectionTheme, { "--site-text": style.text_color, "--site-muted": style.text_color });
  if (section.kind === "hero" && style.background_image) content.hero = { ...content.hero, image: style.background_image };
  return <div ref={ref} id={section.id} data-testid={`section-instance-${section.id}`} data-editor-section={section.id} data-background={!!style.background} data-background-image={!!style.background_image} data-text-color={!!style.text_color} data-spacing={style.spacing} className={`editable-section ${onSelect ? "editor-selectable" : ""}`} style={{ ...sectionTheme, "--section-background": style.background, "--section-text": style.text_color, "--section-image": style.background_image ? `url(${JSON.stringify(assetUrl(style.background_image))})` : undefined }} onClickCapture={onSelect ? (event) => { event.preventDefault(); event.stopPropagation(); onSelect(section.id); } : undefined}>
    <SiteContentContext.Provider value={{ ...content, ...context, services: ["request", "reviews"].includes(section.kind) ? context.formServices : content.services }}>
      {Component ? <Component /> : <CustomSection section={section} link={context.resolveLink} />}
    </SiteContentContext.Provider>
  </div>;
};